import type { OnboardingIntent } from "@/lib/onboarding/onboarding-intent";

/**
 * What each door says while it asks for the same fields.
 *
 * The steps are the same — CPF, cadastro, endereço, código — and so are the rules; only the
 * reason changes. Someone paying for a wedding gift is not "starting their list", and reading
 * that while holding a full bag is being told they came to the wrong place.
 *
 * Kept as data, in one file, rather than as conditionals inside each form: the copy review
 * reads both voices side by side, and a form that takes its words as props has no opinion
 * about who is in front of it.
 */
export type OnboardingVoice = {
  cpf: {
    eyebrow: string;
    /** What comes before the emphasised "CPF" in the heading. */
    titleLead: string;
    /** What comes after the emphasised "CPF" in the heading (e.g. "do cliente"). Defaults to nothing. */
    titleTail?: string;
    lede: string;
    /** The field's label. Defaults to "CPF". */
    fieldLabel?: string;
    /** Under the field, where the person asks themselves why we want this. */
    help: string;
    /** The submit button. Defaults to "Continuar". */
    submitLabel?: string;
    /** The way out above the eyebrow, when the form has one. Defaults to "voltar para a Homepage". */
    exitLabel?: string;
    /** Said when fewer than eleven digits are submitted. Defaults to "Informe os onze dígitos do seu CPF.". */
    incompleteMessage?: string;
  };
  /** The cadastro step's lede. The rest of that form is the same either way. */
  signupLede: string;
};

export const ONBOARDING_VOICE: Record<OnboardingIntent, OnboardingVoice> = {
  host: {
    cpf: {
      eyebrow: "Bem-vindo à sua Lista de Presentes",
      titleLead: "Para começar, informe seu",
      lede: "Seu CPF é a chave para manter sua lista de presentes, seus créditos e todas as informações reunidas em um só lugar.",
      help: "Utilizamos seu CPF para identificar sua lista de presentes e garantir a segurança das suas informações.",
    },
    signupLede: "Seus dados ficam guardados com segurança, e a sua lista começa a ganhar forma.",
  },
  guest: {
    cpf: {
      eyebrow: "Falta pouco para enviar o seu presente",
      titleLead: "Para continuar, informe seu",
      lede: "É com ele que identificamos a sua compra e acompanhamos o presente até a casa de quem você escolheu presentear.",
      help: "Usamos seu CPF apenas para identificar a sua compra.",
    },
    signupLede: "Seus dados ficam guardados com segurança: é para eles que enviamos a confirmação do seu presente.",
  },
};
